<script lang="ts" setup>
import type { ContentBookReader } from '~/models/Content/ContentBookReader'
import type { ContentAuthor } from '~/models/Content/ContentAuthor'
import type { BaseItem } from '~/models/Content/BaseItem'
import { useI18n } from 'vue-i18n'
import { computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useQuery } from '@tanstack/vue-query'
import {
  faBook,
  faEarthEurope,
  faGlasses,
  faSeedling,
  faStars,
  faWaveform
} from '@fortawesome/duotone-regular-svg-icons'
import { setTitle } from '~/utils/dom'
import useLicenseControlStore from '~/stores/licenseControl'
import useAuthStore from '~/stores/auth'
import { Subtree } from '~/models/Content/Subtree'
import { ContentType } from '~/models/Content/ContentType'
import { useUrlBuilder } from '~/composables/useUrlBuilder'
import { useContentEvent } from '~/composables/activity/useContentEvent'
import useContentApi from '~/api/contentApi'
import TopBar from '~/components/utils/TopBar.vue'
import Button from '~/components/ui/Button.vue'
import AuSkeletonWrapper from '~/components/ui/AuSkeletonWrapper.vue'
import AuSkeleton from '~/components/ui/AuSkeleton.vue'
import AuPopover from '~/components/ui/AuPopover.vue'
import NotFound from '~/components/ResourceEngine/Components/NotFound.vue'
import CardRenderer from '~/components/cards/CardRenderer.vue'

const props = defineProps({
  locationId: {
    type: Number,
    required: true,
  }
})

const { t } = useI18n()
const { buildImageUrlByField } = useUrlBuilder()
const { findContents, findImageContent } = useContentApi()
const { checkItemLicense } = useLicenseControlStore()
const { isAuthenticated } = storeToRefs(useAuthStore())

const relatedIds = computed(() => location.value?.relatedContent?.destinationContentIds ?? [])
const hasRelatedIds = computed(() => relatedIds.value.length > 0)
const authorIds = computed(() => location.value?.authors?.destinationContentIds ?? [])
const hasAuthorIds = computed(() => authorIds.value.length > 0)
const imageId = computed(() => Number(location.value?.image?.destinationContentId))
const hasImageId = computed(() => Number.isInteger(imageId.value))

const {
  data: location,
  isLoading,
  isError,
} = useQuery({
  staleTime: Infinity,
  queryKey: computed(() => ['book-reader-summary-view', props.locationId]),
  queryFn: () => findContents<ContentBookReader>({
    contentTypeCriterion: [ContentType.BookReader],
    subtreeCriterion: [Subtree.Content],
    locationIdCriterion: [props.locationId],
  }, 1).then(([content]) => content ?? null),
  retry: 1,
})

const { autoContentEvent } = useContentEvent()
autoContentEvent(location)

const {
  data: related,
} = useQuery({
  enabled: hasRelatedIds,
  staleTime: Infinity,
  queryKey: computed(() => ['book-reader-summary-view-relations', relatedIds]),
  queryFn: () =>
    findContents<BaseItem>({
      contentIdCriterion: relatedIds.value,
      mainLocationCriterion: true,
      subtreeCriterion: [Subtree.Content],
    }, relatedIds.value.length)
})

const {
  data: authors,
} = useQuery({
  enabled: hasAuthorIds,
  staleTime: Infinity,
  queryKey: computed(() => ['book-reader-summary-view-authors', relatedIds]),
  queryFn: () =>
    findContents<ContentAuthor>({
      contentTypeCriterion: [ContentType.Author],
      contentIdCriterion: authorIds.value,
      mainLocationCriterion: true,
      subtreeCriterion: [Subtree.Authors],
    }, authorIds.value.length)
})

const {
  data: image
} = useQuery({
  enabled: hasImageId,
  staleTime: Infinity,
  queryKey: computed(() => ['book-reader-summary-view-image', location.value?.image?.destinationContentId]),
  queryFn: () =>
    findImageContent(Number(location.value?.image?.destinationContentId))
})

watch(location, () => {
  if (!location.value)
    return
  setTitle(location.value.title)
  checkItemLicense(location.value)
}, { immediate: true })
</script>

<template>
  <TopBar
    :item="location"
    :fullscreen="false"
  />
  <NotFound v-if="isError" />
  <template v-else>
    <div
      v-if="location || isLoading"
      class="grid h-full grid-rows-[300px] text-lg md:grid-cols-[4fr_6fr] md:grid-rows-[auto]"
    >
      <div
        class="bg-purple-5 flex place-content-center items-start px-5 py-12 md:px-14"
        :style="{ background: image?.palette }"
      >
        <img
          v-if="location?.image"
          class="top-12 h-full max-h-full md:sticky md:h-auto"
          :src="buildImageUrlByField(location.contentId, 'image')"
          :alt="location.image.alternativeText"
        >
      </div>

      <div
        v-if="location"
        class="box-content flex max-w-prose flex-col gap-6 px-5 py-12 md:px-16"
      >
        <ul class="border-purple-5 flex gap-2 border-b pb-2">
          <li
            v-for="item in [...location.genres, ...location.themes]"
            :key="item"
            class="flex items-center gap-2 content-[''] after:content-['•'] last:after:content-['']"
          >
            {{ item }}
          </li>
        </ul>

        <h1
          class="text-5xl font-bold"
          style="word-break: break-word;"
        >
          {{ location.title }}
        </h1>

        <ul class="flex flex-wrap gap-4">
          <li
            v-for="author in authors"
            :key="author.locationId"
            class="flex-none"
          >
            <AuPopover align="start">
              <template #trigger>
                <button class="text-purple-40 hover:text-seagreen-50 font-bold focus-visible:ring">
                  {{ author.title }}
                </button>
              </template>

              <template #content>
                <article class="bg-purple-5 flex w-96 max-w-full flex-col gap-3 rounded-md p-6">
                  <p class="text-xl font-bold">
                    {{ author.title }}
                  </p>
                  <div
                    class="contents"
                    v-html="author.description"
                  />
                </article>
              </template>
            </AuPopover>
          </li>
        </ul>

        <div v-html="location.description" />

        <Button
          :to="{ name: 'bookreader', params: $route.params }"
          class="self-start"
          size="xl"
          :disabled="!isAuthenticated"
        >
          <font-awesome-icon :icon="faBook" />
          {{ t('bookreader.readBook') }}
        </Button>

        <dl class="bg-purple-5 grid gap-6 rounded-lg p-6">
          <div class="flex gap-2">
            <dt>{{ t('content.published') }}:</dt>
            <dd class="font-bold">
              {{ location.publishYear }}
            </dd>
            <font-awesome-icon
              :icon="faSeedling"
              class="ml-auto text-xl"
            />
          </div>

          <div class="flex gap-2">
            <dt>{{ t('content.language') }}:</dt>
            <dd
              v-for="language in location.languages"
              :key="language"
              class="font-bold"
            >
              {{ language }}
            </dd>
            <font-awesome-icon
              :icon="faEarthEurope"
              class="ml-auto text-xl"
            />
          </div>

          <div class="flex gap-2">
            <dt>{{ t('content.pageCount') }}:</dt>
            <dd class="font-bold">
              {{ location.pageNumbers }}
            </dd>
            <font-awesome-icon
              :icon="faGlasses"
              class="ml-auto text-xl"
            />
          </div>

          <div
            v-if="location.hasAudio"
            class="flex gap-2"
          >
            <dt>{{ t('content.audio') }}:</dt>
            <dd class="font-bold">
              {{ t('content.recordedAudio') }}
            </dd>
            <font-awesome-icon
              :icon="faWaveform"
              class="ml-auto text-xl"
            />
          </div>

          <div
            v-if="location.series.length"
            class="flex gap-2"
          >
            <dt>{{ t('content.inSeries') }}:</dt>
            <dd
              v-for="series in location.series"
              :key="series"
              class="font-bold"
            >
              {{ series }}
            </dd>
            <font-awesome-icon
              :icon="faStars"
              class="ml-auto text-xl"
            />
          </div>
        </dl>

        <template v-if="isAuthenticated && hasRelatedIds">
          <h2 class="text-xl font-bold">
            {{ t('relatedContent') }}
          </h2>

          <ul class="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            <li
              v-for="relatedContent in related"
              :key="relatedContent?.locationId"
            >
              <CardRenderer :resource="relatedContent" />
            </li>
          </ul>
        </template>
      </div>

      <div
        v-else-if="isLoading"
        class="box-content flex max-w-prose flex-col gap-6 px-5 py-12 md:px-16"
      >
        <AuSkeleton />

        <hr class="border-seagreen-10 -mt-3">

        <AuSkeletonWrapper
          :text="t('bookreader.loading')"
          class="contents"
        >
          <AuSkeleton height="3rem" />

          <div class="flex flex-wrap gap-4">
            <AuSkeleton width="33%" />
            <AuSkeleton width="33%" />
          </div>

          <div class="flex flex-col gap-3">
            <AuSkeleton width="50ch" />
            <AuSkeleton width="40ch" />
            <AuSkeleton width="45ch" />
            <AuSkeleton width="43ch" />
            <AuSkeleton width="52ch" />
            <AuSkeleton width="35ch" />
          </div>
        </AuSkeletonWrapper>
      </div>
    </div>
  </template>
</template>
